import { useEffect, useState, useMemo } from "react";

/* ─── Data layer: nodes arranged as a neural-network-style knowledge graph ─── */

interface Node {
  id: string;
  x: number;
  y: number;
  r: number;
  label?: string;
  layer: number;
  pulseDelay: number;
}

interface Edge {
  from: string;
  to: string;
  weight: number; // 0-1, affects opacity/width
}

const buildGraph = () => {
  const nodes: Node[] = [];
  const edges: Edge[] = [];

  // Layer 0 — Data Sources (left)
  const sources = [
    { id: "src-1", x: 40, y: 90, label: "API" },
    { id: "src-2", x: 30, y: 190, label: "DB" },
    { id: "src-3", x: 50, y: 290, label: "STREAM" },
    { id: "src-4", x: 35, y: 380, label: "FILES" },
  ];
  sources.forEach((s, i) =>
    nodes.push({ ...s, r: 5, layer: 0, pulseDelay: i * 0.4 })
  );

  // Layer 1 — Ingestion / ETL
  const ingest = [
    { id: "ing-1", x: 160, y: 130 },
    { id: "ing-2", x: 150, y: 240 },
    { id: "ing-3", x: 165, y: 340 },
  ];
  ingest.forEach((n, i) =>
    nodes.push({ ...n, r: 6, layer: 1, pulseDelay: i * 0.5 + 0.2 })
  );

  // Layer 2 — Transform / Feature Engineering (central cluster)
  const transform = [
    { id: "tf-1", x: 300, y: 100, label: "CLEAN" },
    { id: "tf-2", x: 290, y: 200 },
    { id: "tf-3", x: 310, y: 290, label: "ENRICH" },
    { id: "tf-4", x: 280, y: 380 },
  ];
  transform.forEach((n, i) =>
    nodes.push({ ...n, r: 7, layer: 2, pulseDelay: i * 0.3 + 0.5 })
  );

  // Layer 3 — Model / Intelligence (core)
  const model = [
    { id: "ml-1", x: 460, y: 140, label: "PREDICT" },
    { id: "ml-2", x: 450, y: 250, label: "CLASSIFY" },
    { id: "ml-3", x: 470, y: 350, label: "OPTIMIZE" },
  ];
  model.forEach((n, i) =>
    nodes.push({ ...n, r: 9, layer: 3, pulseDelay: i * 0.4 + 0.8 })
  );

  // Layer 4 — Central brain node
  const brain: Node = {
    id: "brain",
    x: 580,
    y: 245,
    r: 16,
    label: "AI",
    layer: 4,
    pulseDelay: 0,
  };
  nodes.push(brain);

  // Layer 5 — Output / Deploy
  const output = [
    { id: "out-1", x: 710, y: 150, label: "DEPLOY" },
    { id: "out-2", x: 720, y: 260, label: "MONITOR" },
    { id: "out-3", x: 700, y: 370, label: "DECIDE" },
  ];
  output.forEach((n, i) =>
    nodes.push({ ...n, r: 6, layer: 5, pulseDelay: i * 0.3 + 1.2 })
  );

  // Scatter nodes — ambient floating particles for depth
  const scatter = [
    { id: "sc-1", x: 110, y: 60 },
    { id: "sc-2", x: 220, y: 50 },
    { id: "sc-3", x: 380, y: 60 },
    { id: "sc-4", x: 540, y: 80 },
    { id: "sc-5", x: 650, y: 100 },
    { id: "sc-6", x: 100, y: 420 },
    { id: "sc-7", x: 240, y: 440 },
    { id: "sc-8", x: 400, y: 430 },
    { id: "sc-9", x: 560, y: 410 },
    { id: "sc-10", x: 680, y: 430 },
    { id: "sc-11", x: 370, y: 170 },
    { id: "sc-12", x: 520, y: 160 },
    { id: "sc-13", x: 200, y: 320 },
    { id: "sc-14", x: 620, y: 340 },
  ];
  scatter.forEach((n, i) =>
    nodes.push({ ...n, r: 2, layer: -1, pulseDelay: i * 0.25 })
  );

  // ── Edges ──
  // Sources → Ingestion
  edges.push(
    { from: "src-1", to: "ing-1", weight: 0.7 },
    { from: "src-2", to: "ing-1", weight: 0.5 },
    { from: "src-2", to: "ing-2", weight: 0.8 },
    { from: "src-3", to: "ing-2", weight: 0.6 },
    { from: "src-3", to: "ing-3", weight: 0.7 },
    { from: "src-4", to: "ing-3", weight: 0.6 },
    { from: "src-1", to: "ing-2", weight: 0.3 }
  );

  // Ingestion → Transform
  edges.push(
    { from: "ing-1", to: "tf-1", weight: 0.8 },
    { from: "ing-1", to: "tf-2", weight: 0.5 },
    { from: "ing-2", to: "tf-2", weight: 0.7 },
    { from: "ing-2", to: "tf-3", weight: 0.6 },
    { from: "ing-3", to: "tf-3", weight: 0.8 },
    { from: "ing-3", to: "tf-4", weight: 0.7 },
    { from: "ing-1", to: "tf-3", weight: 0.2 },
    { from: "ing-2", to: "tf-1", weight: 0.3 }
  );

  // Transform → Model
  edges.push(
    { from: "tf-1", to: "ml-1", weight: 0.8 },
    { from: "tf-1", to: "ml-2", weight: 0.4 },
    { from: "tf-2", to: "ml-1", weight: 0.5 },
    { from: "tf-2", to: "ml-2", weight: 0.7 },
    { from: "tf-3", to: "ml-2", weight: 0.6 },
    { from: "tf-3", to: "ml-3", weight: 0.8 },
    { from: "tf-4", to: "ml-3", weight: 0.7 },
    { from: "tf-2", to: "ml-3", weight: 0.3 },
    { from: "tf-4", to: "ml-2", weight: 0.2 }
  );

  // Model → Brain
  edges.push(
    { from: "ml-1", to: "brain", weight: 0.9 },
    { from: "ml-2", to: "brain", weight: 0.9 },
    { from: "ml-3", to: "brain", weight: 0.9 }
  );

  // Brain → Output
  edges.push(
    { from: "brain", to: "out-1", weight: 0.8 },
    { from: "brain", to: "out-2", weight: 0.8 },
    { from: "brain", to: "out-3", weight: 0.8 }
  );

  // Cross-layer connections (subtle)
  edges.push(
    { from: "tf-1", to: "tf-3", weight: 0.15 },
    { from: "ml-1", to: "ml-3", weight: 0.15 },
    { from: "out-1", to: "out-3", weight: 0.1 }
  );

  // Scatter connections (very subtle ambient lines)
  edges.push(
    { from: "sc-1", to: "ing-1", weight: 0.08 },
    { from: "sc-2", to: "tf-1", weight: 0.08 },
    { from: "sc-3", to: "tf-1", weight: 0.06 },
    { from: "sc-4", to: "ml-1", weight: 0.06 },
    { from: "sc-5", to: "out-1", weight: 0.08 },
    { from: "sc-6", to: "ing-3", weight: 0.06 },
    { from: "sc-7", to: "tf-4", weight: 0.06 },
    { from: "sc-8", to: "ml-3", weight: 0.05 },
    { from: "sc-9", to: "brain", weight: 0.06 },
    { from: "sc-10", to: "out-3", weight: 0.06 },
    { from: "sc-11", to: "tf-2", weight: 0.05 },
    { from: "sc-12", to: "ml-1", weight: 0.05 },
    { from: "sc-13", to: "tf-3", weight: 0.05 },
    { from: "sc-14", to: "out-2", weight: 0.05 }
  );

  return { nodes, edges };
};

/* ─── Particle paths for animation ─── */
const PARTICLE_PATHS = [
  ["src-1", "ing-1", "tf-1", "ml-1", "brain", "out-1"],
  ["src-2", "ing-2", "tf-2", "ml-2", "brain", "out-2"],
  ["src-3", "ing-3", "tf-3", "ml-3", "brain", "out-3"],
  ["src-4", "ing-3", "tf-4", "ml-3", "brain", "out-3"],
  ["src-1", "ing-2", "tf-3", "ml-2", "brain", "out-1"],
  ["src-3", "ing-2", "tf-1", "ml-1", "brain", "out-2"],
];

const HeroDiagram = ({ className = "" }: { className?: string }) => {
  const [mouseOffset, setMouseOffset] = useState({ x: 0, y: 0 });
  const { nodes, edges } = useMemo(() => buildGraph(), []);
  const nodeMap = useMemo(
    () => Object.fromEntries(nodes.map((n) => [n.id, n])),
    [nodes]
  );

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      const x = (e.clientX / window.innerWidth - 0.5) * 12;
      const y = (e.clientY / window.innerHeight - 0.5) * 8;
      setMouseOffset({ x, y });
    };
    window.addEventListener("mousemove", handleMouseMove, { passive: true });
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  return (
    <div
      className={`absolute inset-0 flex items-center justify-center pointer-events-none overflow-hidden ${className}`}
    >
      {/* Background radial glow */}
      <div
        className="absolute inset-0"
        style={{
          background: `
            radial-gradient(ellipse at 58% 42%, hsl(var(--primary) / 0.10) 0%, transparent 50%),
            radial-gradient(ellipse at 35% 65%, hsl(var(--primary) / 0.06) 0%, transparent 40%),
            radial-gradient(ellipse at 75% 30%, hsl(var(--primary) / 0.04) 0%, transparent 35%)
          `,
        }}
      />

      {/* Floating glow orb */}
      <div
        className="absolute w-[400px] h-[400px] rounded-full animate-glow-pulse"
        style={{
          background:
            "radial-gradient(circle, hsl(var(--primary) / 0.12) 0%, transparent 70%)",
          top: "15%",
          right: "10%",
          transform: `translate(${mouseOffset.x * 0.5}px, ${mouseOffset.y * 0.5}px)`,
          transition: "transform 0.4s ease-out",
        }}
      />

      <svg
        viewBox="0 0 760 480"
        className="w-[600px] h-[360px] md:w-[800px] md:h-[440px] lg:w-[950px] lg:h-[520px]"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        style={{
          transform: `translate(${mouseOffset.x * 0.15}px, ${mouseOffset.y * 0.15}px)`,
          transition: "transform 0.4s ease-out",
        }}
      >
        <defs>
          <filter id="nodeGlow">
            <feGaussianBlur stdDeviation="3" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <filter id="brainGlow">
            <feGaussianBlur stdDeviation="8" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <radialGradient id="brainGrad" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity="0.5" />
            <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity="0.05" />
          </radialGradient>
          <linearGradient id="edgeGrad" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity="0.1" />
            <stop offset="50%" stopColor="hsl(var(--primary))" stopOpacity="0.4" />
            <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity="0.1" />
          </linearGradient>
        </defs>

        {/* ── Edges ── */}
        {edges.map((edge, i) => {
          const from = nodeMap[edge.from];
          const to = nodeMap[edge.to];
          if (!from || !to) return null;
          const opacity = Math.max(0.03, edge.weight * 0.35);
          const width = 0.3 + edge.weight * 0.8;
          // Curved paths for cross-connections
          const dx = to.x - from.x;
          const dy = to.y - from.y;
          const cx1 = from.x + dx * 0.35;
          const cy1 = from.y + dy * 0.1;
          const cx2 = from.x + dx * 0.65;
          const cy2 = to.y - dy * 0.1;
          return (
            <path
              key={`edge-${i}`}
              d={`M${from.x},${from.y} C${cx1},${cy1} ${cx2},${cy2} ${to.x},${to.y}`}
              stroke="hsl(var(--primary))"
              strokeWidth={width}
              opacity={opacity}
              fill="none"
            />
          );
        })}

        {/* ── Traveling particles ── */}
        {PARTICLE_PATHS.map((path, pi) => {
          const pathNodes = path.map((id) => nodeMap[id]).filter(Boolean);
          if (pathNodes.length < 2) return null;
          const d = pathNodes
            .map((n, i) => {
              if (i === 0) return `M${n.x},${n.y}`;
              const prev = pathNodes[i - 1];
              const dx = n.x - prev.x;
              const dy = n.y - prev.y;
              const cx1 = prev.x + dx * 0.35;
              const cy1 = prev.y + dy * 0.1;
              const cx2 = prev.x + dx * 0.65;
              const cy2 = n.y - dy * 0.1;
              return `C${cx1},${cy1} ${cx2},${cy2} ${n.x},${n.y}`;
            })
            .join(" ");
          const pathId = `particle-path-${pi}`;
          const dur = 4 + pi * 0.5;
          return (
            <g key={pathId}>
              <path id={pathId} d={d} fill="none" stroke="none" />
              <circle
                r="2"
                fill="hsl(var(--primary))"
                filter="url(#nodeGlow)"
              >
                <animateMotion
                  dur={`${dur}s`}
                  repeatCount="indefinite"
                  begin={`${pi * 0.8}s`}
                >
                  <mpath href={`#${pathId}`} />
                </animateMotion>
                <animate
                  attributeName="opacity"
                  values="0;0.8;0.8;0"
                  dur={`${dur}s`}
                  repeatCount="indefinite"
                  begin={`${pi * 0.8}s`}
                />
              </circle>
              {/* Second particle offset */}
              <circle
                r="1.5"
                fill="hsl(var(--primary))"
                filter="url(#nodeGlow)"
              >
                <animateMotion
                  dur={`${dur + 1}s`}
                  repeatCount="indefinite"
                  begin={`${pi * 0.8 + dur / 2}s`}
                >
                  <mpath href={`#${pathId}`} />
                </animateMotion>
                <animate
                  attributeName="opacity"
                  values="0;0.5;0.5;0"
                  dur={`${dur + 1}s`}
                  repeatCount="indefinite"
                  begin={`${pi * 0.8 + dur / 2}s`}
                />
              </circle>
            </g>
          );
        })}

        {/* ── Brain node — central glowing hub ── */}
        <circle
          cx={nodeMap["brain"].x}
          cy={nodeMap["brain"].y}
          r="40"
          fill="url(#brainGrad)"
          filter="url(#brainGlow)"
        >
          <animate
            attributeName="r"
            values="38;44;38"
            dur="6s"
            repeatCount="indefinite"
          />
        </circle>
        <circle
          cx={nodeMap["brain"].x}
          cy={nodeMap["brain"].y}
          r="22"
          stroke="hsl(var(--primary))"
          strokeWidth="0.8"
          fill="none"
          opacity="0.3"
        >
          <animate
            attributeName="opacity"
            values="0.2;0.5;0.2"
            dur="4s"
            repeatCount="indefinite"
          />
          <animate
            attributeName="r"
            values="20;24;20"
            dur="5s"
            repeatCount="indefinite"
          />
        </circle>
        <circle
          cx={nodeMap["brain"].x}
          cy={nodeMap["brain"].y}
          r="7"
          fill="hsl(var(--primary))"
          filter="url(#nodeGlow)"
          opacity="0.8"
        >
          <animate
            attributeName="opacity"
            values="0.7;1;0.7"
            dur="3s"
            repeatCount="indefinite"
          />
        </circle>
        <text
          x={nodeMap["brain"].x}
          y={nodeMap["brain"].y + 38}
          textAnchor="middle"
          fill="hsl(var(--primary))"
          opacity="0.6"
          fontSize="9"
          fontFamily="JetBrains Mono, monospace"
          letterSpacing="0.2em"
        >
          AI ENGINE
        </text>

        {/* ── Regular nodes ── */}
        {nodes
          .filter((n) => n.id !== "brain")
          .map((node) => {
            const isScatter = node.layer === -1;
            const hasLabel = !!node.label;
            return (
              <g key={node.id}>
                {/* Outer ring */}
                {!isScatter && (
                  <circle
                    cx={node.x}
                    cy={node.y}
                    r={node.r + 10}
                    stroke="hsl(var(--primary))"
                    strokeWidth="0.4"
                    fill="none"
                    opacity="0.08"
                  >
                    <animate
                      attributeName="opacity"
                      values="0.06;0.18;0.06"
                      dur="5s"
                      repeatCount="indefinite"
                      begin={`${node.pulseDelay}s`}
                    />
                  </circle>
                )}
                {/* Mid ring for larger nodes */}
                {node.r >= 6 && !isScatter && (
                  <circle
                    cx={node.x}
                    cy={node.y}
                    r={node.r + 4}
                    stroke="hsl(var(--primary))"
                    strokeWidth="0.5"
                    fill="none"
                    opacity="0.12"
                  >
                    <animate
                      attributeName="opacity"
                      values="0.1;0.3;0.1"
                      dur="4s"
                      repeatCount="indefinite"
                      begin={`${node.pulseDelay + 0.3}s`}
                    />
                  </circle>
                )}
                {/* Core dot */}
                <circle
                  cx={node.x}
                  cy={node.y}
                  r={isScatter ? 1.5 : node.r > 7 ? 4 : 3}
                  fill="hsl(var(--primary))"
                  filter={isScatter ? undefined : "url(#nodeGlow)"}
                  opacity={isScatter ? 0.15 : 0.55}
                >
                  <animate
                    attributeName="opacity"
                    values={
                      isScatter ? "0.1;0.25;0.1" : "0.45;0.8;0.45"
                    }
                    dur={isScatter ? "6s" : "3.5s"}
                    repeatCount="indefinite"
                    begin={`${node.pulseDelay}s`}
                  />
                </circle>
                {/* Label */}
                {hasLabel && !isScatter && (
                  <text
                    x={node.x}
                    y={node.y + node.r + 18}
                    textAnchor="middle"
                    fill="hsl(var(--primary))"
                    opacity="0.4"
                    fontSize="8"
                    fontFamily="JetBrains Mono, monospace"
                    letterSpacing="0.12em"
                  >
                    {node.label}
                  </text>
                )}
              </g>
            );
          })}

        {/* ── Hexagonal grid overlay (very subtle) ── */}
        {Array.from({ length: 8 }).map((_, row) =>
          Array.from({ length: 12 }).map((_, col) => {
            const x = col * 68 + (row % 2 ? 34 : 0) - 10;
            const y = row * 62 + 10;
            if (x < 0 || x > 760 || y < 0 || y > 480) return null;
            return (
              <circle
                key={`hex-${row}-${col}`}
                cx={x}
                cy={y}
                r="0.8"
                fill="hsl(var(--primary))"
                opacity="0.04"
              />
            );
          })
        )}
      </svg>
    </div>
  );
};

export default HeroDiagram;
